// Base properties that are automatically included in every Amplitude event. Includes rebook context, attribution, and session data.

import { getAttribution } from '@/lib/attribution';
import { getRebookCode } from '@/lib/utils/rebookStorage';
import config from '@/constants/config';
import type { AmplitudeBaseProperties } from './types';

function getRebookDiscountPercent(timestamp: number): number {
  const daysSinceRebook = Math.floor((Date.now() - timestamp) / (1000 * 60 * 60 * 24));

  if (daysSinceRebook <= 30) return 12;
  if (daysSinceRebook <= 60) return 10;
  return 5;
}

function detectPageType(): AmplitudeBaseProperties['page_type'] {
  if (typeof window === 'undefined') return undefined;

  const path = window.location.pathname;

  if (path === '/') return 'home';
  if (path.startsWith('/vacation-rentals/')) {
    const segments = path.split('/').filter(Boolean);
    return segments.length === 2 ? 'town_hub' : 'listing';
  }
  if (path.startsWith('/checkout')) return 'checkout';
  if (path.startsWith('/confirmation')) return 'confirmation';
  if (path.startsWith('/trips')) return 'trips';
  if (path.startsWith('/favorites')) return 'favorites';
  if (path.startsWith('/store')) return 'store';
  if (path.startsWith('/review')) return 'review';
  if (path.startsWith('/quotes')) return 'quote';

  return undefined;
}

function getEnvironment(): 'development' | 'staging' | 'production' {
  const env = config.environment?.toLowerCase();

  if (env === 'production' || env === 'prod') return 'production';
  if (env === 'staging' || env === 'stage') return 'staging';
  return 'development';
}

// Cache for checkout session ID (persists for the current checkout flow)
let checkoutSessionId: string | undefined;

/** Sets the checkout session ID for linking events within a checkout flow */
export function setCheckoutSessionId(id: string): void {
  checkoutSessionId = id;
}

/** Gets the current checkout session ID */
export function getCheckoutSessionId(): string | undefined {
  return checkoutSessionId;
}

/** Clears the checkout session ID (call after purchase or when leaving checkout) */
export function clearCheckoutSessionId(): void {
  checkoutSessionId = undefined;
}

export function getBaseProperties(): AmplitudeBaseProperties {
  const rebookData = getRebookCode();
  const isRebookGuest = rebookData !== null;

  const attribution = getAttribution();

  return {
    environment: getEnvironment(),
    is_rebook_guest: isRebookGuest,
    ...(rebookData && {
      rebook_code: rebookData.code,
      rebook_discount_percent: getRebookDiscountPercent(rebookData.timestamp),
    }),
    ...(attribution?.channel && { attribution_channel: attribution.channel }),
    ...(attribution?.source && { attribution_source: attribution.source }),
    page_url: typeof window !== 'undefined' ? window.location.href : '',
    page_type: detectPageType(),
    ...(checkoutSessionId && { checkout_session_id: checkoutSessionId }),
  };
}
